import React from 'react';
import { BookOpen, CheckCircle, AlertTriangle, UserX } from 'lucide-react';

const SubjectLoadPanel = ({ subjectAllocations = [], scheduledCounts = {} }) => {
  const safeAllocations = (subjectAllocations || []).filter(Boolean);

  if (safeAllocations.length === 0) {
    return (
      <div style={{ background: '#FFF7ED', border: '1px solid #FFEDD5', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', fontSize: '13px', color: '#C2410C', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <AlertTriangle size={18} />
        <span>No subjects allocated to this Class & Section. Configure subject allocations first under Subject Management.</span>
      </div>
    );
  }

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '18px 20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <BookOpen size={18} color="#2563EB" /> Weekly Subject Requirements
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '14px' }}>
        {safeAllocations.map(alloc => {
          if (!alloc) return null;
          const req = alloc.periodsPerWeek || 5;
          const subjectName = alloc.subjectName || alloc.subjectCode || 'Subject';
          const scheduled = scheduledCounts[subjectName] || 0;
          const isTeacherMissing = !alloc.teacherName || alloc.teacherName === 'Unassigned' || alloc.teacherName === 'Unassigned Faculty';

          // Progress percentage capped at 100% for progress bar
          const pct = Math.min(100, Math.round((scheduled / req) * 100));

          // State evaluation
          const isExactComplete = scheduled === req;
          const isOverAllocated = scheduled > req;
          const isNotStarted = scheduled === 0;

          // Status Badge Styling
          let statusBadge = {
            text: `${scheduled} of ${req} scheduled • ${req - scheduled} more periods needed`,
            color: '#2563EB',
            bg: '#EFF6FF',
            barBg: '#2563EB',
            icon: null
          };

          if (isNotStarted) {
            statusBadge = {
              text: `0 of ${req} scheduled • Not started`,
              color: '#64748B',
              bg: '#F1F5F9',
              barBg: '#94A3B8',
              icon: null
            };
          } else if (isExactComplete) {
            statusBadge = {
              text: `${scheduled} of ${req} scheduled • ✓ Requirement completed`,
              color: '#166534',
              bg: '#DCFCE7',
              barBg: '#166534',
              icon: <CheckCircle size={13} color="#166534" />
            };
          } else if (isOverAllocated) {
            statusBadge = {
              text: `${scheduled} of ${req} scheduled • ⚠ Extra periods scheduled`,
              color: '#B45309',
              bg: '#FEF3C7',
              barBg: '#D97706',
              icon: <AlertTriangle size={13} color="#D97706" />
            };
          }

          return (
            <div 
              key={alloc.id || subjectName} 
              style={{ 
                background: isTeacherMissing ? '#FEF2F2' : '#F8FAFC', 
                border: isTeacherMissing ? '1px solid #FECACA' : '1px solid #E2E8F0', 
                borderRadius: '10px', 
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: '800', color: '#0F172A' }}>{subjectName}</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: statusBadge.color }}>
                    {scheduled}/{req} periods
                  </span>
                </div>

                {/* Teacher Row */}
                <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Teacher: {isTeacherMissing ? (
                    <span style={{ fontWeight: '700', color: '#DC2626', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <UserX size={13} /> Teacher not assigned
                    </span>
                  ) : (
                    <span style={{ fontWeight: '700', color: '#334155' }}>{alloc.teacherName}</span>
                  )}
                </div>
              </div>

              <div>
                {/* Progress Bar */}
                <div style={{ height: '6px', backgroundColor: '#E2E8F0', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, backgroundColor: statusBadge.barBg, transition: 'width 0.3s ease' }} />
                </div>

                {/* Subtitle Status */}
                <div style={{ 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  color: statusBadge.color, 
                  marginTop: '6px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px' 
                }}>
                  {statusBadge.icon}
                  <span>{statusBadge.text}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubjectLoadPanel;
